import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { CheckMark2Small, CrossLarge } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import {
  CalendarGrid,
  CalendarSelectMenu,
  buildCalendarMonth,
  createCalendarFormat,
  startOfDayUtc,
  todayUtc,
  utcDate,
} from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import type {
  CalendarDayModel,
  CalendarDayState,
  CalendarFormat,
} from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { buildYearOptions } from "./buildYearOptions";

type RangeField = "from" | "until";

interface RangeBuildOptions {
  from: Date | null;
  until: Date | null;
  minDate?: Date;
  maxDate?: Date;
  includeWeekends: boolean;
  today: Date;
}

/** Resolve one month's grid with range-aware day states (selected endpoints + inRange interior). */
function buildRangeWeeks(
  year: number,
  month: number,
  format: CalendarFormat,
  { from, until, minDate, maxDate, includeWeekends, today }: RangeBuildOptions,
): { weekdayLabels: string[]; weeks: CalendarDayModel[][] } {
  const built = buildCalendarMonth(utcDate(year, month, 1), format);
  const weekdayLabels = includeWeekends ? built.weekdayLabels : built.weekdayLabels.slice(0, 5);
  const rawWeeks = includeWeekends ? built.weeks : built.weeks.map((week) => week.slice(0, 5));

  const fromTime = from ? startOfDayUtc(from).getTime() : null;
  const untilTime = until ? startOfDayUtc(until).getTime() : null;
  const lo = fromTime !== null && untilTime !== null ? Math.min(fromTime, untilTime) : null;
  const hi = fromTime !== null && untilTime !== null ? Math.max(fromTime, untilTime) : null;
  const minTime = minDate ? startOfDayUtc(minDate).getTime() : null;
  const maxTime = maxDate ? startOfDayUtc(maxDate).getTime() : null;

  // Drop week-rows that contain no day of the target month (e.g. a leading/trailing
  // all-adjacent-month row). Computed after the weekend column-slice so a Mo–Fr row
  // whose only in-month day is a hidden weekend is also dropped. Yields natural height:
  // 4 rows (e.g. a Monday-start February), 5 (most months), or 6 (a weekend-on month
  // starting Sat/Sun). buildCalendarMonth stays fixed-6, so DatePicker is unaffected.
  const monthRows = rawWeeks.filter((week) => week.some((cell) => cell.date.getUTCMonth() === month));

  const weeks = monthRows.map((week) =>
    week.map((cell): CalendarDayModel => {
      const time = cell.date.getTime();
      const outOfMonth = cell.date.getUTCMonth() !== month;
      const disabled = outOfMonth || (minTime !== null && time < minTime) || (maxTime !== null && time > maxTime);

      let state: CalendarDayState = "default";
      if (disabled) state = "disabled";
      else if (time === fromTime || time === untilTime) state = "selected";
      else if (lo !== null && hi !== null && time > lo && time < hi) state = "inRange";

      return {
        date: cell.date,
        label: cell.label,
        ariaLabel: cell.ariaLabel,
        state,
        isToday: !outOfMonth && time === today.getTime(),
      };
    }),
  );

  return { weekdayLabels, weeks };
}

const Root = styled("div")({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  minHeight: 0,
});

// Floats over the top of the calendar with a downward fade (mirror of BottomDock) so the months
// scroll under the close/year controls instead of being hard-cut by an opaque band.
const TopDock = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[12],
  padding: `0 ${theme.fieldSalesTwo.spacing[4]} ${theme.fieldSalesTwo.spacing[32]}`,
  background: `linear-gradient(to bottom, ${theme.fieldSalesTwo.colors.background} 58%, rgba(240,238,237,0))`,
  pointerEvents: "none",
  "& > *": { pointerEvents: "auto" },
}));

const CloseButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.fieldSalesTwo.spacing[48],
  height: theme.fieldSalesTwo.spacing[48],
  flexShrink: 0,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  cursor: "pointer",
  color: theme.fieldSalesTwo.colors.neutral[900],
  WebkitTapHighlightColor: "transparent",
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  "& svg": { width: 18, height: 18 },
  ...pressInteraction(theme, { scale: 0.97 }),
}));

const ScrollArea = styled("div")(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
  // Stay scrollable but hide the scrollbar — with 12 months it would otherwise be permanent.
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  // Clearance so content scrolls out from under the floating top/bottom docks.
  paddingTop: 80,
  paddingBottom: 96,
}));

const MonthCard = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `${theme.fieldSalesTwo.spacing[20]} ${theme.fieldSalesTwo.spacing[20]} ${theme.fieldSalesTwo.spacing[24]}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  // A jumped-to card lands below the floating TopDock (mirrors ScrollArea's paddingTop).
  scrollMarginTop: 80,
}));

// Floats over the calendar (doesn't take layout space) with a fade so the days scroll under it,
// matching the Figma. Decorative area is click-through; only the bar + confirm capture pointers.
const BottomDock = styled("div")(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10,
  display: "flex",
  padding: `${theme.fieldSalesTwo.spacing[32]} 0 0`,
  background: `linear-gradient(to top, ${theme.fieldSalesTwo.colors.background} 62%, rgba(240,238,237,0))`,
  pointerEvents: "none",
  "& > *": { pointerEvents: "auto" },
}));

// Single glass capsule that wraps BOTH date fields and the confirm circle (Figma 2172-12443).
const Bar = styled("div")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "stretch",
  height: 68,
  padding: theme.fieldSalesTwo.spacing[6],
  borderRadius: theme.fieldSalesTwo.radius.circular,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  overflow: "hidden",
}));

const Field = styled("button")<{ active?: boolean }>(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  textAlign: "left",
  WebkitTapHighlightColor: "transparent",
  transition: `background-color ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  variants: [
    {
      props: { active: true },
      style: { backgroundColor: "rgba(19, 19, 19, 0.06)" },
    },
  ],
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: `-${theme.fieldSalesTwo.spacing[4]}`,
  },
}));

const FieldLabel = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[700],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
}));

const FieldValue = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
}));

const ConfirmButton = styled("button")<{ enabled?: boolean }>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "center",
  marginLeft: theme.fieldSalesTwo.spacing[8],
  width: 56,
  height: 56,
  flexShrink: 0,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  border: "none",
  background: theme.fieldSalesTwo.colors.neutral[300],
  color: theme.fieldSalesTwo.colors.neutral[500],
  cursor: "not-allowed",
  WebkitTapHighlightColor: "transparent",
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}, background-color ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  "& svg": { width: 22, height: 22 },
  variants: [
    {
      props: { enabled: true },
      style: {
        background: theme.fieldSalesTwo.colors.status.success.main,
        color: "#ffffff",
        cursor: "pointer",
        "&:active": { transform: "scale(0.96)" },
      },
    },
  ],
}));

export interface DateRangePickerValue {
  start: Date;
  end: Date;
}

export interface DateRangePickerProps {
  /** Currently applied range (UTC midnight), or null for none. */
  value?: DateRangePickerValue | null;
  /** Fired only once both ends are chosen and the user confirms. */
  onChange: (range: DateRangePickerValue) => void;
  /** Fired when the close (X) button is pressed. */
  onClose?: () => void;
  minDate?: Date;
  maxDate?: Date;
  /** When true, show Sa/So columns. Defaults to false (Mo–Fr, matching the Figma). */
  includeWeekends?: boolean;
}

/**
 * Two-month range picker built on the shared Calendar primitives. The user fills the active field
 * (From, then Until); tapping the From/Until segments switches which end is being chosen. Confirm
 * stays disabled until both ends are set. Dates are UTC midnight (matching DatePicker / the backend
 * LocalDate boundary).
 */
export function DateRangePicker({
  value,
  onChange,
  onClose,
  minDate,
  maxDate,
  includeWeekends = false,
}: DateRangePickerProps) {
  const t = useTranslations("fieldSalesTwo.dateRangePicker");
  const { locale } = useLocale();
  const today = useMemo(() => todayUtc(), []);

  const [from, setFrom] = useState<Date | null>(() => (value ? startOfDayUtc(value.start) : null));
  const [until, setUntil] = useState<Date | null>(() => (value ? startOfDayUtc(value.end) : null));
  const [activeField, setActiveField] = useState<RangeField>("from");

  const [year, setYear] = useState(() => (from ? from.getUTCFullYear() : today.getUTCFullYear()));

  const format = useMemo(() => createCalendarFormat(locale), [locale]);
  const monthFormatter = useMemo(() => new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }), [locale]);
  const valueFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }),
    [locale],
  );

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: index,
        label: monthFormatter.format(utcDate(year, index, 1)),
      })),
    [monthFormatter, year],
  );

  const yearOptions = useMemo(() => buildYearOptions(today), [today]);

  const handleSelectDay = (date: Date) => {
    const day = startOfDayUtc(date);
    if (activeField === "from") {
      setFrom(day);
      if (until && day.getTime() > startOfDayUtc(until).getTime()) setUntil(null);
      setActiveField("until");
    } else if (from && day.getTime() < startOfDayUtc(from).getTime()) {
      // An 'Until' earlier than 'From' → swap so from ≤ until.
      setUntil(from);
      setFrom(day);
    } else {
      setUntil(day);
    }
  };

  const monthGrids = useMemo(
    () =>
      Array.from({ length: 12 }, (_, m) =>
        buildRangeWeeks(year, m, format, { from, until, minDate, maxDate, includeWeekends, today }),
      ),
    [year, format, from, until, minDate, maxDate, includeWeekends, today],
  );

  const monthRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollToMonth = useCallback((monthIndex: number, smooth: boolean) => {
    monthRefs.current[monthIndex]?.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "start" });
  }, []);

  // Position the viewport at the relevant month before first paint (mount-only, instant).
  useLayoutEffect(() => {
    // Always open positioned at the current calendar month (today's month index).
    scrollToMonth(today.getUTCMonth(), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switching the year rebuilds all 12 months; jump back to January (skip the initial mount).
  const prevYearRef = useRef(year);
  useEffect(() => {
    if (prevYearRef.current === year) return;
    const goingBack = year < prevYearRef.current;
    prevYearRef.current = year;
    // Going back a year lands on December (end of year); going forward, January (start) —
    // so crossing the boundary reads as a continuous scroll across the year change.
    scrollToMonth(goingBack ? 11 : 0, true);
  }, [year, scrollToMonth]);

  const labelFor = (date: Date | null): string => {
    if (!date) return t("pickDate", { defaultValue: "Datum wählen" });
    if (date.getTime() === today.getTime()) return t("today", { defaultValue: "Heute" });
    return valueFormatter.format(date);
  };

  const canConfirm = from !== null && until !== null;

  const handleConfirm = () => {
    if (from && until) onChange({ start: from, end: until });
  };

  return (
    <Root>
      <TopDock>
        <CloseButton type="button" aria-label={t("close", { defaultValue: "Schließen" })} onClick={onClose}>
          <CrossLarge aria-hidden="true" />
        </CloseButton>
        <CalendarSelectMenu
          variant="pill"
          align="right"
          showItemIcons={false}
          options={yearOptions}
          value={year}
          onSelect={setYear}
          triggerLabel={t("selectYear", { defaultValue: "Jahr wählen" })}
        />
      </TopDock>

      <ScrollArea>
        {monthGrids.map((grid, m) => (
          <MonthCard
            key={m}
            ref={(el) => {
              monthRefs.current[m] = el;
            }}
          >
            <CalendarSelectMenu
              options={monthOptions}
              value={m}
              onSelect={(month) => scrollToMonth(month, true)}
              triggerLabel={t("selectMonth", { defaultValue: "Monat wählen" })}
            />
            <CalendarGrid weekdayLabels={grid.weekdayLabels} weeks={grid.weeks} onSelectDay={handleSelectDay} />
          </MonthCard>
        ))}
      </ScrollArea>

      <BottomDock>
        <Bar>
          <Field type="button" active={activeField === "from"} onClick={() => setActiveField("from")}>
            <FieldLabel>{t("from", { defaultValue: "Von" })}</FieldLabel>
            <FieldValue>{labelFor(from)}</FieldValue>
          </Field>
          <Field type="button" active={activeField === "until"} onClick={() => setActiveField("until")}>
            <FieldLabel>{t("until", { defaultValue: "Bis" })}</FieldLabel>
            <FieldValue>{labelFor(until)}</FieldValue>
          </Field>
          <ConfirmButton
            type="button"
            enabled={canConfirm}
            disabled={!canConfirm}
            aria-label={t("apply", { defaultValue: "Übernehmen" })}
            onClick={handleConfirm}
          >
            <CheckMark2Small aria-hidden="true" />
          </ConfirmButton>
        </Bar>
      </BottomDock>
    </Root>
  );
}
